import React from 'react'

const Typography = React.forwardRef(
  (
    {
      children,
      variant = 'body1',
      component,
      sx = {},
      className = '',
      color = 'inherit',
      align = 'inherit',
      textAlign,
      gutterBottom = false,
      noWrap = false,
      paragraph = false,
      ...props
    },
    ref
  ) => {
    // Filter out custom props that shouldn't be passed to DOM
    const { textAlign: _, ...domProps } = props

    // Map MUI variants to HTML elements and default styles
    const getVariantProps = (variant) => {
      switch (variant) {
        case 'h1':
          return {
            element: 'h1',
            defaultStyles: {
              fontSize: '2.125rem',
              fontWeight: 300,
              lineHeight: 1.167,
              letterSpacing: '-0.01562em'
            }
          }
        case 'h2':
          return {
            element: 'h2',
            defaultStyles: {
              fontSize: '1.5rem',
              fontWeight: 300,
              lineHeight: 1.334,
              letterSpacing: '-0.00833em'
            }
          }
        case 'h3':
          return {
            element: 'h3',
            defaultStyles: {
              fontSize: '1.25rem',
              fontWeight: 400,
              lineHeight: 1.6,
              letterSpacing: '0em'
            }
          }
        case 'h4':
          return {
            element: 'h4',
            defaultStyles: {
              fontSize: '1.125rem',
              fontWeight: 400,
              lineHeight: 1.667,
              letterSpacing: '0.00735em'
            }
          }
        case 'h5':
          return {
            element: 'h5',
            defaultStyles: {
              fontSize: '1.0625rem',
              fontWeight: 400,
              lineHeight: 1.6,
              letterSpacing: '0em'
            }
          }
        case 'h6':
          return {
            element: 'h6',
            defaultStyles: {
              fontSize: '1rem',
              fontWeight: 500,
              lineHeight: 1.75,
              letterSpacing: '0.0075em'
            }
          }
        case 'subtitle1':
          return {
            element: 'h6',
            defaultStyles: {
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: 1.75,
              letterSpacing: '0.00938em'
            }
          }
        case 'subtitle2':
          return {
            element: 'h6',
            defaultStyles: {
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: 1.57,
              letterSpacing: '0.00714em'
            }
          }
        case 'body1':
          return {
            element: 'p',
            defaultStyles: {
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: 1.5,
              letterSpacing: '0.00938em'
            }
          }
        case 'body2':
          return {
            element: 'p',
            defaultStyles: {
              fontSize: '0.875rem',
              fontWeight: 400,
              lineHeight: 1.43,
              letterSpacing: '0.01071em'
            }
          }
        case 'button':
          return {
            element: 'span',
            defaultStyles: {
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: 1.75,
              letterSpacing: '0.02857em',
              textTransform: 'uppercase'
            }
          }
        case 'caption':
          return {
            element: 'span',
            defaultStyles: {
              fontSize: '0.75rem',
              fontWeight: 400,
              lineHeight: 1.66,
              letterSpacing: '0.03333em'
            }
          }
        case 'overline':
          return {
            element: 'span',
            defaultStyles: {
              fontSize: '0.75rem',
              fontWeight: 400,
              lineHeight: 2.66,
              letterSpacing: '0.08333em',
              textTransform: 'uppercase'
            }
          }
        default:
          return {
            element: 'p',
            defaultStyles: {
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: 1.5,
              letterSpacing: '0.00938em'
            }
          }
      }
    }

    const { element: Element, defaultStyles } = getVariantProps(variant)
    const Component = component || Element

    // Build styles object
    const styles = {
      ...defaultStyles,
      color: color !== 'inherit' ? color : undefined,
      textAlign: textAlign || (align !== 'inherit' ? align : undefined),
      marginBottom: paragraph ? '1em' : gutterBottom ? '0.35em' : undefined,
      whiteSpace: noWrap ? 'nowrap' : undefined,
      overflow: noWrap ? 'hidden' : undefined,
      textOverflow: noWrap ? 'ellipsis' : undefined,
      ...sx
    }

    return (
      <Component ref={ref} className={className} style={styles} {...domProps}>
        {children}
      </Component>
    )
  }
)

Typography.displayName = 'Typography'

export default Typography
